import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Flex,
  Radio,
  RadioGroup,
  Text,
  TextArea,
  TextField,
  styled,
} from '@kandji-inc/nectar-ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  useAcceptRisk,
  useAcceptRiskUpdate,
} from 'src/features/edr/vulnerability/hooks/use-accept-risk';
import type { Vulnerability } from 'src/features/edr/vulnerability/vulnerability.types';
import { i18n } from 'src/i18n';
import z from 'zod';

enum RiskAction {
  ACCEPT = 'accept',
  UNACCEPT = 'unaccept',
}

const RiskAcceptanceRequestSchema = z.object({
  action: z
    .enum(['accept', 'unaccept'])
    .describe('Action to perform - either accept or unaccept risk'),
  enforced_until: z
    .date()
    .nullable()
    .describe(
      'Date until which the risk is accepted (for "accept" action only)',
    ),
  ticketing_link: z
    .string()
    .nullable()
    .refine(
      (val) => !val || val.match(/^https?:\/\/.+/),
      'Please enter a valid URL',
    )
    .describe('Link to related ticket or issue'),
  comment: z
    .string()
    .nullable()
    .describe('Comment about the risk acceptance decision'),
});

type RiskAcceptanceRequest = z.infer<typeof RiskAcceptanceRequestSchema>;

const StyledContainer = styled(Flex, {
  gap: '$5',
  flexDirection: 'column',
  padding: '10px $5 10px $5',
});

const StyledFooter = styled(Flex, {
  gap: '$2',
  position: 'absolute',
  bottom: 0,
  marginInline: '-20px',
  paddingInline: '20px',
  padding: '$3 $4',
  borderTop: '1px solid $neutral20',
  backgroundColor: '$neutral0',
  justifyContent: 'right',
});

const AcceptRisk = ({
  selectedVulnerability,
}: { selectedVulnerability: Vulnerability }) => {
  // @ts-ignore
  const riskAcceptance = selectedVulnerability?.risk_acceptance;
  const isRiskAccepted = riskAcceptance?.action === RiskAction.ACCEPT;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<RiskAcceptanceRequest>({
    resolver: zodResolver(RiskAcceptanceRequestSchema),
    defaultValues: {
      action: null,
      enforced_until: null,
      ticketing_link: riskAcceptance?.ticketing_link || '',
      comment: riskAcceptance?.comment || '',
    },
  });
  const { mutateAsync: acceptRisk, isPending } = useAcceptRisk();
  const { mutateAsync: acceptRiskUpdate, isPending: isSavePending } =
    useAcceptRiskUpdate();
  const [isAccepted, setIsAccepted] = useState(isRiskAccepted);

  const enforcedUntil =
    watch('enforced_until') === null ? 'indefinitely' : 'specificdate';
  const commentValue = watch('comment') || '';

  const handleSave = async () => {
    const result = await acceptRiskUpdate({
      cve_id: selectedVulnerability.cve_id,
      // @ts-ignore
      risk_acceptance_id: selectedVulnerability.risk_acceptance?.id,
      ...getValues(),
    });
  };

  const onSubmit = async (data: RiskAcceptanceRequest) => {
    const result = await acceptRisk({
      cve_id: selectedVulnerability.cve_id,
      ...data,
    });

    setIsAccepted((prev) => result.data.action === RiskAction.ACCEPT);
  };

  const handleAcceptRisk = (action: RiskAction) => {
    setValue('action', action);
    handleSubmit(onSubmit)();
  };

  const handleEnforcedUntilChange = (value: string) => {
    setValue('enforced_until', value === 'indefinitely' ? null : new Date());
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StyledContainer>
        <Flex flow="column" gap="md">
          <Text variant="secondary">
            <b>{i18n.t('Enforcement timeframe')}</b>
          </Text>

          <RadioGroup
            value={enforcedUntil}
            onValueChange={handleEnforcedUntilChange}
            orientation="vertical"
          >
            <Radio value="indefinitely" label={i18n.t('Indefinitely')} />
            <Radio
              value="specificdate"
              label={i18n.t('Accept risk until a specific date')}
            />
          </RadioGroup>
        </Flex>

        <TextField
          label={i18n.t('Ticketing link')}
          isOptional={true}
          placeholder={i18n.t('Enter a URL of any corresponding tickets')}
          {...register('ticketing_link')}
          state={errors.ticketing_link ? 'error' : 'default'}
          hint={
            errors.ticketing_link
              ? { label: errors.ticketing_link.message }
              : { label: '' }
          }
        />

        <TextArea
          label={i18n.t('Comment')}
          isOptional={true}
          placeholder={i18n.t(
            'Provide a brief description of why this CVE will be ignored',
          )}
          value={commentValue}
          {...register('comment')}
        />
      </StyledContainer>

      <StyledFooter wFull>
        {isAccepted ? (
          <Flex gap="sm">
            <Button
              type="button"
              onClick={() => handleAcceptRisk(RiskAction.UNACCEPT)}
              loading={isPending}
              icon={isPending && { position: 'left' }}
            >
              {i18n.t('Un-accept risk')}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              variant="primary"
              loading={isSavePending}
              icon={isSavePending && { position: 'left' }}
              css={{ minWidth: '120px' }}
            >
              {i18n.t('Save')}
            </Button>
          </Flex>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={() => handleAcceptRisk(RiskAction.ACCEPT)}
            loading={isPending}
            icon={isPending && { position: 'left' }}
          >
            {i18n.t('Accept risk')}
          </Button>
        )}
      </StyledFooter>
    </form>
  );
};

export { AcceptRisk };
